// R115 calendar hub — the registry contract (orchestrator-owned; additive-only this round).
//
// The Calendar is ONE timeline with five VIEWS (presets). A view never owns code paths of its
// own: it only chooses which LAYERS are on/dim/off, which STATS (KPI cards that double as the
// single active filter) show, which RAIL providers fill the "📥 To do" worklist, and which
// inspector SECTIONS open first. Features (MTR, maintenance, guest actions, ops) add files that
// call the register* functions below plus ONE import line in ./features.ts — they never edit
// the page's render loop.
//
// Surfaces (three sizes only):
//   quick popover  (≤320px anchored; bottom sheet <640px)  — chip summaries, turn assign/move, WO book
//   inspector      (the #stc-panel host, push layout ≥1200px, overlay 900–1199, full sheet <900)
//                  — one entity at a time with a back stack: stay, block, listing, wo, turn,
//                    inspection, task, lead, tenant, date …
//   form sheet     (z 300, sticky footer marked [data-bottom-bar]) — every create/edit form
// z-order: panel 150 < pop 250 < sheet 300 < PhotoQueueSheet 330 < kebab 340 < toast 400
//          < InfoAskPill 800 < lightbox 1200. Nothing opened from the calendar uses 9000.

import type { CalData, Stay, Property } from './types';

export type ViewId = 'stays' | 'ops' | 'guests' | 'mtr' | 'health';
export type OfficeRole = 'admin' | 'director' | 'manager' | 'remote_ops';
export type Tone = 'info' | 'ok' | 'warn' | 'bad' | 'muted' | 'accent';
export type LayerState = 'on' | 'dim' | 'off';

/** Anything the inspector can show. kind = 'stay'|'block'|'listing'|'wo'|'turn'|'inspection'|'task'|'lead'|'tenant'|'date'|… */
export interface EntityRef { kind: string; id: string; listing_id?: string; date?: string }

/** A day-anchored chip on a property row (packed into the ops sub-lanes). end is exclusive (default start+1). */
export interface ChipSpec {
  key: string; entity: EntityRef; listing_id: string; start: string; end?: string;
  tone: Tone; glyph?: string; label?: string; title: string;
  initials?: string[]; badge?: string; ring?: boolean; dashed?: boolean; strike?: boolean; movable?: boolean;
}
/** A property-state badge next to the row label (★ score, ⚑ open issues, 📦 supply requests …). */
export interface BadgeSpec { key: string; glyph: string; text?: string; title: string; tone: Tone; entity?: EntityRef }
export interface StayDecoration { cls?: string; badges?: BadgeSpec[]; titleSuffix?: string }

export interface LayerQuery { from: string; to: string; as_of: string }
/** A layer = one kind of data drawn on the timeline. Layers with `server` are fetched together from
 *  GET /api/stays/calendar/layers?l=<id>,<id>&from&to (one request per view switch/window, cached,
 *  cleared on every base reload). `load` is for data served elsewhere (e.g. /api/mtr/calendar). */
export interface Layer {
  id: string; label: string; swatch: string; roles?: OfficeRole[];
  scope: 'day' | 'row' | 'stay';
  server?: string;
  load?: (q: LayerQuery, signal: AbortSignal) => Promise<unknown>;
  chips?: (p: Property, ctx: CalendarCtx) => ChipSpec[];
  badges?: (p: Property, ctx: CalendarCtx) => BadgeSpec[];
  decorateStay?: (s: Stay, ctx: CalendarCtx) => StayDecoration | null;
  count?: (ctx: CalendarCtx) => number | null;
}

/** A stat = a KPI card that is also a filter. Views list ≤6 stat keys; one stat can be active at a time. */
export interface StatValue { value: string | number | null; tone?: 'good' | 'warn' | 'bad'; sub?: string }
export interface StatSpec {
  key: string; label: string; icon: string; layer?: string;
  compute: (ctx: CalendarCtx) => StatValue | null;
  match?: { rows?: (p: Property, ctx: CalendarCtx) => boolean; stays?: (s: Stay, ctx: CalendarCtx) => boolean; chips?: (c: ChipSpec, ctx: CalendarCtx) => boolean };
}

/** The worklist rail ("📥 To do"): items that have no day yet or need attention. Clicking an item opens its inspector. */
export interface RailItem { key: string; entity: EntityRef; listing_id: string | null; title: string; sub?: string; tone?: Tone; date?: string | null; search?: string;
  /** R115 integ fix (additive): a row that opens a LIST (e.g. "All 80 unscheduled"), not a to-do — shown, never counted. */
  meta?: boolean }
export interface RailProvider { key: string; label: string; layer?: string; items: (ctx: CalendarCtx) => RailItem[] }

/** Inspector = how one entity kind is shown in the panel. Sections and actions compose onto it. */
export interface Inspector {
  kind: string;
  title: (ref: EntityRef, data: unknown, ctx: CalendarCtx) => string;
  sub?: (ref: EntityRef, data: unknown, ctx: CalendarCtx) => string;
  load?: (ref: EntityRef, signal: AbortSignal, ctx: CalendarCtx) => Promise<unknown>;
  busy?: () => boolean; reset?: () => void;
}
export interface Section {
  inspector: string; key: string; title: string; order: number; openIn?: ViewId[];
  when: (ref: EntityRef, data: unknown, ctx: CalendarCtx) => boolean;
  render: (ref: EntityRef, data: unknown, ctx: CalendarCtx) => string;
  bind?: (root: HTMLElement, ref: EntityRef, data: unknown, ctx: CalendarCtx) => void;
  busy?: () => boolean;
}
/** Actions live on an inspector kind. inspector 'date' = the create chooser for a listing+day
 *  (ref.kind 'date', ref.listing_id, ref.date; data = DateCell). The first action whose primaryIn
 *  includes the current view is the one blue .btn-primary; up to 2 more are .btn-secondary on
 *  desktop; the rest go in the ⋯ Kebab. On phones only the primary is a button. danger: this action
 *  is never made the primary just because it comes first (the row then has no blue button). */
export interface Action {
  key: string; inspector: string; label: string; icon: string; order: number;
  roles?: OfficeRole[]; primaryIn?: ViewId[]; danger?: boolean;
  when: (ref: EntityRef, data: unknown, ctx: CalendarCtx) => boolean;
  run: (ref: EntityRef, data: unknown, ctx: CalendarCtx) => void;
}
export interface DateCell { listing_id: string; date: string; past: boolean; taken: boolean; stay_id: string | null }

/** Chip kinds: how a chip opens and (optionally) moves. Built-in kinds 'turn' and 'wo' are registered by core. */
export interface ChipKind {
  kind: string; legend?: string;
  open: (ref: EntityRef, anchor: HTMLElement, ctx: CalendarCtx) => void;
  move?: { canDrop: (ref: EntityRef, date: string, ctx: CalendarCtx) => boolean; onDrop: (ref: EntityRef, date: string, ctx: CalendarCtx) => void | Promise<void> };
}

/** A view (preset). Presets are DATA in ./presets.ts (orchestrator-owned); unknown layer/stat/rail keys are skipped. */
export interface View {
  id: ViewId | string; parent?: ViewId; label: string; short: string; icon: string; blurb: string; order: number;
  roles: OfficeRole[];
  layers: Record<string, LayerState>;
  stats: string[];
  rail?: string[];
  rowFilter?: (p: Property, ctx: CalendarCtx) => boolean;
  rowSort?: (a: Property, b: Property, ctx: CalendarCtx) => number;
  rowSub?: (p: Property, ctx: CalendarCtx) => string;
  openSections?: string[];
  createOrder?: string[];
}

export interface PostResult<T = any> { ok: boolean; status: number; json: T | null; error?: string }

/** What a feature module may use. Implemented by the page (core builder) and by ./testkit for tests. */
export interface CalendarCtx {
  readonly data: CalData; readonly view: string; readonly role: string;
  readonly asOf: string; readonly from: string; readonly to: string;
  layer<T = unknown>(id: string): T | null;
  layerError(id: string): string | null;
  /** A registered layer for the loaded window even when this view doesn't draw it (fetched once, cached; null when it
   *  failed or the role can't read it). Resolves with fresh data when a refresh of it is in flight. */
  ensureLayer<T = unknown>(id: string): Promise<T | null>;
  /** Every home on the calendar with the search box ignored (the "Which home?" pickers). */
  allProperties(): Property[];
  property(listingId: string): Property | null;
  staysOf(listingId: string): Stay[];
  nightTaken(listingId: string, date: string): boolean;
  /** Resolves once the base payload AND the named / cached layers are fresh (a module may read ctx.layer() right after). */
  reload(opts?: { base?: boolean; layers?: string[]; inspector?: boolean }): Promise<void>;
  toast(msg: string): void;
  toastUndo(msg: string, undo: () => void): void;
  openInspector(ref: EntityRef): void;
  back(): void;
  closeInspector(): void;
  openPop(anchor: HTMLElement | DOMRect, html: string, opts?: { width?: number; cls?: string; onClose?: () => void }): HTMLElement;
  closePop(): void;
  /** Form sheet host — never redrawn by timeline/panel redraws; markDirty() makes the page veto auto-refresh. */
  openSheet(html: string, opts?: { title?: string; onClose?: () => void }): HTMLElement;
  closeSheet(): void;
  markDirty(dirty: boolean): void;
  openChooser(listingId: string, date: string): void;
  /** Touch-safe move/book: shows the listing's next 21 days in the sheet and calls onPick(date). */
  pickDay(o: { listing_id: string; label: string; allowPast?: boolean; allowTaken?: boolean; onPick: (date: string) => void }): void;
  focus(o: { date?: string; listing_id?: string }): void;
  go(page: string, hubTab?: string): void;
  actorName(): string;
  esc(s: unknown): string;
  /** POST JSON with the 409 {needs_confirm} → confirm → resend {override:true} handshake used across HK/inspections. */
  postWithOverride<T = any>(url: string, body: Record<string, unknown>, confirmText?: (j: any) => string): Promise<PostResult<T>>;
  fmt: { md(d: string): string; wmd(d: string): string; addDays(d: string, n: number): string; diffDays(a: string, b: string): number };
}

// ── registry implementation (tiny on purpose) ──────────────────────────────────
const layers = new Map<string, Layer>();
const stats = new Map<string, StatSpec>();
const rails = new Map<string, RailProvider>();
const inspectors = new Map<string, Inspector>();
const sections: Section[] = [];
const actions: Action[] = [];
const chipKinds = new Map<string, ChipKind>();
const views = new Map<string, View>();
const busyFns = new Map<string, () => boolean>();
const cssBlocks = new Map<string, string>();

export function registerLayer(l: Layer): void { layers.set(l.id, l); }
export function registerStat(s: StatSpec): void { stats.set(s.key, s); }
export function registerRail(r: RailProvider): void { rails.set(r.key, r); }
export function registerInspector(i: Inspector): void { inspectors.set(i.kind, i); }
export function registerSection(s: Section): void {
  const at = sections.findIndex(x => x.inspector === s.inspector && x.key === s.key);
  if (at >= 0) sections.splice(at, 1, s); else sections.push(s);
}
export function registerAction(a: Action): void {
  const at = actions.findIndex(x => x.inspector === a.inspector && x.key === a.key);
  if (at >= 0) actions.splice(at, 1, a); else actions.push(a);
}
export function registerChipKind(k: ChipKind): void { chipKinds.set(k.kind, k); }
export function registerView(v: View): void { views.set(String(v.id), v); }
export function registerBusy(id: string, fn: () => boolean): void { busyFns.set(id, fn); }
export function registerCss(id: string, css: string): void { cssBlocks.set(id, css); }

const allowed = (roles: OfficeRole[] | undefined, role: string) => !roles || !roles.length || (roles as string[]).includes(role) || role === 'dev';

export function getLayer(id: string): Layer | null { return layers.get(id) || null; }
export function allLayers(role: string): Layer[] { return [...layers.values()].filter(l => allowed(l.roles, role)); }
export function getStat(key: string): StatSpec | null { return stats.get(key) || null; }
export function getRail(key: string): RailProvider | null { return rails.get(key) || null; }
export function getInspector(kind: string): Inspector | null { return inspectors.get(kind) || null; }
export function sectionsFor(kind: string): Section[] { return sections.filter(s => s.inspector === kind).sort((a, b) => a.order - b.order); }
export function actionsFor(kind: string, role: string): Action[] { return actions.filter(a => a.inspector === kind && allowed(a.roles, role)).sort((a, b) => a.order - b.order); }
export function getChipKind(kind: string): ChipKind | null { return chipKinds.get(kind) || null; }
export function getView(id: string): View | null { return views.get(id) || null; }
export function viewsFor(role: string): View[] { return [...views.values()].filter(v => allowed(v.roles, role)).sort((a, b) => a.order - b.order); }
export function anyBusy(): boolean { for (const fn of busyFns.values()) { try { if (fn()) return true; } catch { /* a broken module never blocks refresh forever */ } } return false; }
export function allCss(): string { return [...cssBlocks.values()].join('\n'); }

/** Pick the primary / secondary / overflow split for an inspector's action row. */
export function splitActions(list: Action[], view: string, secondaryMax = 2): { primary: Action | null; secondary: Action[]; more: Action[] } {
  const primary = list.find(a => a.primaryIn && a.primaryIn.includes(view as ViewId)) || list.find(a => !a.danger) || null;
  const rest = list.filter(a => a !== primary);
  return { primary, secondary: rest.slice(0, secondaryMax), more: rest.slice(secondaryMax) };
}

/** Tests only: wipe every registration. */
export function __resetRegistry(): void {
  layers.clear(); stats.clear(); rails.clear(); inspectors.clear(); sections.length = 0; actions.length = 0;
  chipKinds.clear(); views.clear(); busyFns.clear(); cssBlocks.clear();
}
